<template>
  <ErrorPage v-if="isNotFound" type="notFound" />
  <LoadingWrapper v-else :state="status">
    <div class="flex flex-col gap-8">
      <section class="flex flex-col gap-2">
        <h2 class="text-title-large">{{ $t('portal.link.title') }}</h2>
        <p class="text-body-medium text-on-surface-variant">{{ $t('portal.link.description') }}</p>
        <div
          v-if="portalUrl"
          class="bg-surface-container rounded-large mt-1 flex items-center gap-2 p-4"
        >
          <p class="text-body-large grow tracking-widest break-all select-all">{{ portalUrl }}</p>
          <BaseIconButton v-tooltip="$t('common.copy')" icon="content_copy" @click="copyLink" />
        </div>
        <p v-else class="text-body-medium text-on-surface-variant">{{ $t('portal.link.empty') }}</p>
        <div class="mt-1 flex flex-wrap gap-2">
          <BaseTextButton
            v-if="!portalLinkToken"
            color="tonal"
            size="M"
            :disabled="isSavingLink"
            @click="generateLink"
          >
            {{ $t('portal.link.generate') }}
          </BaseTextButton>

          <BaseTextButton
            v-if="portalLinkToken"
            size="M"
            icon="qr_code"
            color="tonal"
            @click="openDisplay"
          >
            {{ $t('portal.display.action') }}
          </BaseTextButton>
          <BaseTextButton
            v-if="portalLinkToken"
            size="M"
            icon="print"
            color="tonal"
            @click="openPrint"
          >
            {{ $t('portal.print.action') }}
          </BaseTextButton>
          <BaseTextButton
            v-if="portalLinkToken"
            color="outlined"
            size="M"
            icon="refresh"
            class="ml-auto"
            :disabled="isSavingLink"
            @click="openRegenerateLink"
          >
            {{ $t('portal.link.regenerate') }}
          </BaseTextButton>
        </div>
      </section>
    </div>

    <BaseDialog
      type="basic"
      :is-open="isRegenerateLinkOpen"
      :title="$t('portal.link.regenerateTitle')"
      :description="$t('portal.link.regenerateDescription')"
      @close="closeRegenerateLink"
    >
      <template #dismiss>
        <BaseTextButton @click="closeRegenerateLink" size="M">{{
          $t('common.cancel')
        }}</BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton
          :disabled="isSavingLink"
          size="M"
          icon="refresh"
          @click="confirmRegenerateLink"
        >
          {{ $t('portal.link.regenerateConfirm') }}
        </BaseTextButton>
      </template>
    </BaseDialog>

    <BaseDialog
      type="basic"
      :is-open="memberToRegenerate != undefined"
      :title="$t('portal.accessCode.regenerateTitle')"
      :description="
        $t('portal.accessCode.regenerateDescription', {
          name: memberToRegenerate ? memberName(memberToRegenerate) : '',
        })
      "
      @close="closeRegenerateCode"
    >
      <template #dismiss>
        <BaseTextButton @click="closeRegenerateCode" size="M">{{
          $t('common.cancel')
        }}</BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton :disabled="isSavingCode" size="M" @click="confirmRegenerateCode">
          {{ $t('portal.accessCode.regenerateConfirm') }}
        </BaseTextButton>
      </template>
    </BaseDialog>
  </LoadingWrapper>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import ErrorPage from '@/modules/app/pages/ErrorPage.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import { ApiRequestStatus } from '@/core/models/api/api-request-status'
  import { useSnackStore } from '@/stores/snack.store'
  import type { TeamPortalMember } from '../models/team-portal'
  import { useTeamPortal } from '../composables/use-team-portal'

  const props = defineProps<{
    teamId: string
  }>()

  const router = useRouter()
  const { t } = useI18n()
  const snackStore = useSnackStore()
  const {
    portalLinkToken,
    portalUrl,
    status,
    linkStatus,
    codeStatus,
    isNotFound,
    saveLink,
    saveMemberCode,
  } = useTeamPortal(() => props.teamId)

  const isRegenerateLinkOpen = ref(false)
  const memberToRegenerate = ref<TeamPortalMember>()

  const isSavingLink = computed(() => linkStatus.value === ApiRequestStatus.Loading)
  const isSavingCode = computed(() => codeStatus.value === ApiRequestStatus.Loading)

  const memberName = (member: TeamPortalMember) => {
    return [member.firstName, member.lastName].filter(Boolean).join(' ')
  }

  const copyLink = () => {
    if (!portalUrl.value) {
      return
    }
    navigator.clipboard.writeText(portalUrl.value)
    snackStore.showInfo(t('portal.link.copied'))
  }

  const generateLink = async () => {
    await saveLink(false)
  }

  const openRegenerateLink = () => {
    isRegenerateLinkOpen.value = true
  }

  const closeRegenerateLink = () => {
    isRegenerateLinkOpen.value = false
  }

  const confirmRegenerateLink = async () => {
    const saved = await saveLink(true)
    if (saved) {
      closeRegenerateLink()
    }
  }

  const openPrint = () => {
    router.push({ name: 'team-portal-print', params: { teamId: props.teamId } })
  }

  const openDisplay = () => {
    const { href } = router.resolve({
      name: 'team-portal-display',
      params: { teamId: props.teamId },
    })
    window.open(href, '_blank', 'noopener,noreferrer')
  }

  const closeRegenerateCode = () => {
    memberToRegenerate.value = undefined
  }

  const confirmRegenerateCode = async () => {
    if (!memberToRegenerate.value) {
      return
    }
    const saved = await saveMemberCode(memberToRegenerate.value.memberId)
    if (saved) {
      closeRegenerateCode()
    }
  }
</script>
